import {
  AuthConfig,
  DomainDeleteData,
  DomainSwitchData,
  DomainUserRemoveData,
  L1Config,
  L1L3AuthService,
  parseWebSocketMessage,
  WebSocketEventType,
  WebSocketService
} from 'ngx-auth-util-lib';
import { environment } from 'src/environments/local/environment';

import { Component, OnDestroy, OnInit } from '@angular/core';

import { MatomoConfigService } from './services/matomo-config.service';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WebsocketActionDialogComponent } from './components/websocket-action-dialog/websocket-action-dialog.component';
import { DomainHandlerService } from './services/domain-handler-service';
import { MultiDomainMgmtService } from './services/multi-domain-mgmt.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  private wsActionDialogRef?: MatDialogRef<WebsocketActionDialogComponent>;

  version = environment.appVersion;
  environment = environment.envName;

  constructor(
    private l1l3AuthService: L1L3AuthService,
    private websocketService: WebSocketService,
    private matomoConfigService: MatomoConfigService,
    private domainHandlerService: DomainHandlerService,
    private multiDomainMgmtService: MultiDomainMgmtService,
    private dialog: MatDialog,
    private titleService: Title,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    const l1Config = new L1Config(
      'L1_STORAGE_KEY',
      environment.auth_L1.scope,
      environment.auth_L1.client_id,
      environment.auth_L1.endpoint.host + environment.auth_L1.endpoint.paths.prefix,
      {
        auth: environment.auth_L1.endpoint.paths.login,
        token: environment.auth_L1.endpoint.paths.token,
        publicKey: environment.auth_L1.endpoint.paths.certs,
        logout: environment.auth_L1.endpoint.paths.logout,
        account: environment.auth_L1.endpoint.paths.account
      },
      'ID_TOKEN_HINT_STORAGE_KEY',
      environment.redirect.host + environment.redirect.route,
      environment.redirect.host
    );
    const l3Config = new AuthConfig(
      'L3_STORAGE_KEY',
      environment.backend.scope,
      environment.backend.client_id,
      environment.backend.endpoint.host + environment.backend.endpoint.paths.prefix,
      {
        auth: environment.backend.endpoint.paths.token,
        logout: environment.backend.endpoint.paths.logout
      }
    );
    this.l1l3AuthService.initAuth(l1Config, l3Config, [environment.backend.endpoint.host]);

    this.setupWebsocketService();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap(route => route.data)
      )
      .subscribe(data => {
        this.titleService.setTitle(data['title']);
      });
  }

  ngOnDestroy(): void {
    this.websocketService.close();
    this.subscriptions?.unsubscribe();
  }

  /**
   * Similar to other app's but intentionally not in library because of routing- and app-hanlding reasons.
   */
  private setupWebsocketService() {
    this.subscriptions.add(
      this.websocketService.messages$.subscribe({
        next: message => {
          // make sure the event can be handled
          const parsedMessage = parseWebSocketMessage(message);

          // sourceTabId prevents websocket-message-handling from the same application which could result in performing wrong actions.
          if (
            parsedMessage &&
            !this.wsActionDialogRef &&
            parsedMessage.data.sourceTabId != this.l1l3AuthService.sourceTabId
          ) {
            if (message.eventType == WebSocketEventType.DOMAIN_SWITCH) {
              this.handleDomainSwitch(parsedMessage.data as DomainSwitchData);
            } else if (message.eventType == WebSocketEventType.DOMAIN_USER_REMOVE) {
              const data = parsedMessage.data as DomainUserRemoveData;
              this.handleRemovedFromDomain(
                data.leftDomainId,
                data.hasDomainsLeft,
                this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_REMOVE.TITLE'),
                this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_REMOVE.DESCRIPTION', {
                  companyName: data.leftCompanyName
                }),
                this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_REMOVE.BUTTON_TITLE')
              );
            } else if (message.eventType == WebSocketEventType.DOMAIN_DELETE) {
              const data = parsedMessage.data as DomainDeleteData;
              this.handleRemovedFromDomain(
                data.removedDomainId,
                data.hasDomainsLeft,
                this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_DELETE.TITLE'),
                this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_DELETE.DESCRIPTION', {
                  companyName: data.removedCompanyName
                }),
                this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_DELETE.BUTTON_TITLE')
              );
            }
          }
        }
      })
    );

    this.subscriptions.add(
      this.l1l3AuthService.l1AccessTokenChanged.subscribe({
        next: token => {
          if (token) {
            this.websocketService.connect(
              environment.backend.wsEndpoint,
              token,
              environment.backend.wsHeartbeatIntervalMs
            );
          }
        }
      })
    );
  }

  private handleDomainSwitch(data: DomainSwitchData) {
    this.l1l3AuthService.clearL3Token();
    const profile = this.multiDomainMgmtService.getSharedMultiDomainProfileValue();
    const activeDomain = profile?.domains?.find(d => d.active);

    if (activeDomain?.domainId != data.domainId) {
      this.wsActionDialogRef = this.dialog.open(WebsocketActionDialogComponent, {
        disableClose: true,
        width: '560px',
        data: {
          title: this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_SWITCH.TITLE'),
          description: this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_SWITCH.DESCRIPTION'),
          buttonTitle: this.translateService.instant('WEBSOCKET_ACTION.DOMAIN_SWITCH.BUTTON_TITLE')
        }
      });
      this.wsActionDialogRef.afterClosed().subscribe({ next: () => this.domainHandlerService.reloadApplication() });
    }
  }

  private handleRemovedFromDomain(
    removedDomainId: string,
    hasDomainsLeft: boolean,
    dialogTitle: string,
    dialogDescription: string,
    dialogButtontitle: string
  ) {
    const profile = this.multiDomainMgmtService.getSharedMultiDomainProfileValue();
    const activeDomain = profile?.domains?.find(d => d.active);

    if (activeDomain?.domainId == removedDomainId) {
      this.wsActionDialogRef = this.dialog.open(WebsocketActionDialogComponent, {
        disableClose: true,
        width: '560px',
        data: {
          title: dialogTitle,
          description: dialogDescription,
          buttonTitle: dialogButtontitle
        }
      });
      this.l1l3AuthService.clearL3Token();
      if (hasDomainsLeft) {
        this.wsActionDialogRef.afterClosed().subscribe({
          next: () => {
            this.domainHandlerService.reloadApplication();
          }
        });
      } else {
        this.wsActionDialogRef.afterClosed().subscribe({
          next: () => {
            window.location.href = environment.links.applications.l2.host + environment.links.applications.l2.domains;
          }
        });
      }
    } else {
      this.multiDomainMgmtService.updateMultiDomainProfile();
    }
  }
}
